import { useQuery } from 'urql';

import {
  GeographySummaryMapDataDocument,
  MapCoordsGranularity,
  type MapCoordsReq,
} from '@revelio/data-access';

import { isCountry, isRegion } from '../../Chart/Map/utils/location-type-utils';
import { DataProviderQueryStatus } from '../types';

export type LocationData = {
  id: number;
  name: string;
  lat: number;
  lon: number;
  granularity: MapCoordsGranularity;
};

export type CoordinateResolverResult = {
  data: LocationData[];
  loading: boolean;
  error: unknown;
};

/**
 * A map provider's result once its coordinates count: a point needs one, so a
 * pending lookup is still loading and a failed one is an error, never an
 * empty map.
 */
export function withCoordinateStatus(
  status: DataProviderQueryStatus,
  coords: Pick<CoordinateResolverResult, 'loading' | 'error'>
): { status: DataProviderQueryStatus; error?: Error } {
  if (coords.loading) return { status: DataProviderQueryStatus.Fetching };
  if (coords.error && status === DataProviderQueryStatus.Success) {
    return {
      status: DataProviderQueryStatus.Error,
      error:
        coords.error instanceof Error
          ? coords.error
          : new Error('Map coordinates could not be loaded'),
    };
  }
  return { status };
}

export type GranularityWithIds = {
  granularity: MapCoordsGranularity;
  ids: number[];
};

export type EntityWithLabelAndId = {
  id?: number;
  label?: string;
  name?: string;
  short_name?: string;
  long_name?: string;
  entity_id?: string | number;
};

export type FiltersWithGranularities = {
  msa?: number[];
  country?: number[];
  region?: number[];
};

export function extractGranularitiesWithIds(
  entitiesOrFilters: EntityWithLabelAndId[] | FiltersWithGranularities,
  options?: {
    getLabel?: (entity: EntityWithLabelAndId) => string;
  }
): GranularityWithIds[] {
  const { getLabel } = options || {};

  if (!Array.isArray(entitiesOrFilters)) {
    const filters = entitiesOrFilters as FiltersWithGranularities;
    const granularities: GranularityWithIds[] = [];
    const hasMsa = filters?.msa && filters.msa.length > 0;
    const hasCountry = filters?.country && filters.country.length > 0;
    const hasRegion = filters?.region && filters.region.length > 0;

    if (hasMsa && filters.msa) {
      granularities.push({
        granularity: MapCoordsGranularity.Msa,
        ids: filters.msa,
      });
    }
    if (hasCountry && filters.country) {
      granularities.push({
        granularity: MapCoordsGranularity.Country,
        ids: filters.country,
      });
    }
    if (hasRegion && filters.region) {
      granularities.push({
        granularity: MapCoordsGranularity.Region,
        ids: filters.region,
      });
    }

    return granularities;
  }

  // Label-based categorization (Screener/Transition)
  const entities = entitiesOrFilters as EntityWithLabelAndId[];
  const regionIds: number[] = [];
  const countryIds: number[] = [];
  const msaIds: number[] = [];

  entities.forEach((entity) => {
    const defaultGetLabel = (e: EntityWithLabelAndId) => {
      return (
        e.label ||
        e.name ||
        e.short_name ||
        e.long_name ||
        String(e.entity_id || e.id)
      )
        .toLowerCase()
        .trim();
    };

    const label = getLabel ? getLabel(entity) : defaultGetLabel(entity);
    let entityId = entity.id;
    if (typeof entity.entity_id === 'number') {
      entityId = entity.entity_id;
    } else if (typeof entity.entity_id === 'string') {
      entityId = Number(entity.entity_id);
    }

    if (entityId === undefined || entityId === null) {
      console.warn('Entity missing valid ID:', entity);
      return;
    }

    // All Non-Metro items are MSAs (even if the base name suggests otherwise)
    if (label.includes('non-metro') || label.includes('nonmetro')) {
      msaIds.push(entityId);
    } else if (isRegion(label)) {
      regionIds.push(entityId);
    } else if (isCountry(label)) {
      countryIds.push(entityId);
    } else {
      msaIds.push(entityId);
    }
  });

  const granularities: GranularityWithIds[] = [];

  if (regionIds.length > 0) {
    granularities.push({
      granularity: MapCoordsGranularity.Region,
      ids: regionIds,
    });
  }
  if (countryIds.length > 0) {
    granularities.push({
      granularity: MapCoordsGranularity.Country,
      ids: countryIds,
    });
  }
  if (msaIds.length > 0) {
    granularities.push({
      granularity: MapCoordsGranularity.Msa,
      ids: msaIds,
    });
  }

  return granularities;
}

// Cache to avoid redundant API calls
const coordinateCache = new Map<string, LocationData>();

function createCacheKey(id: number, granularity: MapCoordsGranularity): string {
  return `${id}-${granularity}`;
}

function writeToCache(locations: LocationData[]): void {
  locations.forEach((loc) => {
    const cacheKey = createCacheKey(loc.id, loc.granularity);
    if (!coordinateCache.has(cacheKey)) {
      coordinateCache.set(cacheKey, loc);
    }
  });
}

export const useCoordinateResolverForMixedGranularities = (
  granularitiesWithIds: GranularityWithIds[]
): CoordinateResolverResult => {
  const allIds = granularitiesWithIds.flatMap((g) => g.ids);
  const uniqueIds = Array.from(
    new Set(allIds.filter((id) => typeof id === 'number'))
  );

  // Group IDs by granularity for the request
  const idToGranularityMap = new Map<number, MapCoordsGranularity>();
  granularitiesWithIds.forEach((g) => {
    g.ids.forEach((id) => {
      idToGranularityMap.set(id, g.granularity);
    });
  });

  const request: MapCoordsReq = {
    region: [],
    country: [],
    msa: [],
  };

  const cachedLocations: LocationData[] = [];
  const idsToRequest: number[] = [];

  uniqueIds.forEach((id) => {
    const granularity =
      idToGranularityMap.get(id) || MapCoordsGranularity.Region;
    const cacheKey = createCacheKey(id, granularity);
    const cached = coordinateCache.get(cacheKey);
    if (cached) {
      cachedLocations.push(cached);
    } else {
      idsToRequest.push(id);
    }
  });

  idsToRequest.forEach((id) => {
    const granularity =
      idToGranularityMap.get(id) || MapCoordsGranularity.Region;
    if (granularity === MapCoordsGranularity.Msa) {
      request.msa?.push({ id, granularity });
    } else if (granularity === MapCoordsGranularity.Country) {
      request.country?.push({ id, granularity });
    } else {
      request.region?.push({ id, granularity });
    }
  });

  const [{ data, fetching, error }] = useQuery({
    query: GeographySummaryMapDataDocument,
    variables: { query: request },
    pause: idsToRequest.length === 0,
  });

  const transformedData: LocationData[] = [];

  const pushAll = (
    arr:
      | Array<Array<{ id: number; name: string; lat: number; lon: number }>>
      | undefined
      | null,
    gran: MapCoordsGranularity
  ) => {
    arr?.forEach((group) => {
      group.forEach((item) => {
        transformedData.push({
          id: item.id,
          name: item.name,
          lat: item.lat,
          lon: item.lon,
          granularity: gran,
        });
      });
    });
  };

  pushAll(data?.mapCoords?.msa, MapCoordsGranularity.Msa);
  pushAll(data?.mapCoords?.country, MapCoordsGranularity.Country);
  pushAll(data?.mapCoords?.region, MapCoordsGranularity.Region);

  const dedupedMap = new Map<number, LocationData>();
  transformedData.forEach((loc) => {
    if (!dedupedMap.has(loc.id)) dedupedMap.set(loc.id, loc);
  });

  const fetchedData = Array.from(dedupedMap.values());

  if (fetchedData.length) {
    writeToCache(fetchedData);
  }

  const mergedMap = new Map<number, LocationData>();
  // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
  cachedLocations.forEach((loc) => mergedMap.set(loc.id, loc));
  fetchedData.forEach((loc) => {
    if (!mergedMap.has(loc.id)) mergedMap.set(loc.id, loc);
  });

  const finalData = Array.from(mergedMap.values());

  return {
    data: finalData,
    loading: idsToRequest.length > 0 ? fetching : false,
    error,
  };
};
